/**
 * Image Editor crash diagnostics (VistoeraTASK-00111 P4/P7/P13-P17).
 *
 * TASK-00103 gave the image editor a LOCAL error boundary
 * (`ImageEditorErrorBoundary`) so a crash inside the editor never takes
 * down the whole Canvas — confirmed working by the user's own
 * `imgedit-mu5hw9wl-ul2i2o` screenshot. But that boundary's `errorId` was
 * (deliberately, at the time) just a random reference with zero
 * correlation to *what actually broke* — P4's "an error ID is not a
 * diagnostic system" finding.
 *
 * This module is the fix: a small, plain, mutable snapshot object that
 *   - `DualPaneCutoutEditor` (the shared editor CORE) updates as it moves
 *     through its own initialization phases (`EditorPhase` below) and as
 *     source/canvas/ready state changes — see its own `updateDiagnostics`
 *     calls.
 *   - `CanvasImageEditorOverlay` (the Canvas-specific SHELL) updates with
 *     shell-only state (`activeTool`) it alone knows about.
 *   - `ImageAssetNode` creates ONE fresh instance per editor mount (via
 *     `useMemo` keyed on `[editorOpen, editorInstanceKey]` — see that
 *     file) and hands the SAME ref object to both the boundary (as a
 *     prop, read in `componentDidCatch`) and the shell (threaded through
 *     as a prop into the core) — no module-level singleton, no
 *     generation-id lookup table: whichever editor instance is currently
 *     mounted under a given boundary is, by construction, the only writer
 *     of that boundary's own diagnostics ref.
 *
 * P6/P8 — deliberately NOT here: raw signed URLs, image bytes/Base64,
 * credentials/tokens, full user prompts, or a rendered stack trace for the
 * end-user UI. `SourceKind` below is a closed enum of ROUTE TYPES, never a
 * URL. Consumers that also want the raw `Error`/React `componentStack` for
 * a `console.error` (developer-only, P7/P10) hold those separately — this
 * snapshot is pure structured *state*, not the exception itself.
 */

/** Mirrors the editor's real async initialization sequence (P13). Not a
 * rewrite of `DualPaneCutoutEditor`'s control flow — just names for the
 * phases that flow already passes through, so a crash's `EDITOR_PHASE_AT_
 * CRASH` means something concrete instead of "somewhere during loading". */
export type EditorPhase =
  | 'IDLE'
  | 'LOADING_SOURCE'
  | 'DECODING_SOURCE'
  | 'INITIALIZING_CANVAS'
  | 'INITIALIZING_MASK'
  | 'INITIALIZING_HISTORY'
  | 'READY'
  | 'SAVING'
  | 'ERROR'

/** P15 — "URL only as redacted/route type", never the actual signed URL or
 * even the un-signed same-origin path (which can still embed an asset
 * UUID, P8). */
export type SourceKind =
  | 'UNKNOWN'
  | 'CHAIN_PIXELS_PROXY'
  | 'SITE_ASSET_MASTER_PIXELS'

export interface CanvasDimensions {
  width: number
  height: number
}

export interface EditorDiagnosticsSnapshot {
  /** P17 — unique per real (re)mount of the editor shell (bumped by
   * `ImageAssetNode`'s own `editorInstanceKey`-driven remount AND by a
   * genuinely fresh open/close cycle) — never reused across two different
   * mounted instances, so two crashes never look like "the same editor
   * run" in a diagnostic log even if their random `errorId` prefix were
   * ever to collide. */
  editorMountGeneration: string
  phase: EditorPhase
  sourceKind: SourceKind
  sourceReady: boolean
  pixelsReady: boolean
  /** P15 — set once, right after `img.decode()`/`onload` resolves. */
  sourceNaturalWidth: number | null
  sourceNaturalHeight: number | null
  imageCanvasWidth: number
  imageCanvasHeight: number
  maskCanvasWidth: number
  maskCanvasHeight: number
  leftViewCanvasWidth: number
  leftViewCanvasHeight: number
  rightViewCanvasWidth: number
  rightViewCanvasHeight: number
  /** Shell-only (P17) — `null` while the core has no shell-level tool
   * concept wired in (e.g. `ChainEditorModal`, which does not thread this
   * ref through at all). */
  activeTool: string | null
  /** Last terminal `onSourceError` message class — P64/P66's "this is an
   * EXPECTED load failure, not a programmer crash" signal, kept here only
   * so a genuine LATER crash's diagnostic can show "source loaded fine,
   * this is unrelated" instead of leaving the reader to guess. Never the
   * raw error text from a fetch (which can carry a URL) — just a fixed
   * small set of classifications, see `classifySourceErrorMessage`. */
  lastSourceErrorClass: string | null
}

export function createEditorDiagnosticsSnapshot(): EditorDiagnosticsSnapshot {
  return {
    editorMountGeneration: `edmg-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`,
    phase: 'IDLE',
    sourceKind: 'UNKNOWN',
    sourceReady: false,
    pixelsReady: false,
    sourceNaturalWidth: null,
    sourceNaturalHeight: null,
    imageCanvasWidth: 0,
    imageCanvasHeight: 0,
    maskCanvasWidth: 0,
    maskCanvasHeight: 0,
    leftViewCanvasWidth: 0,
    leftViewCanvasHeight: 0,
    rightViewCanvasWidth: 0,
    rightViewCanvasHeight: 0,
    activeTool: null,
    lastSourceErrorClass: null,
  }
}

/** A ref-like box — deliberately NOT `React.MutableRefObject` at the type
 * level (no React import in this pure module) even though every real
 * caller passes an actual `useRef(...)` result; keeps this file usable
 * from `mediaLoader.ts`-style non-component code/tests too. */
export interface EditorDiagnosticsRef {
  current: EditorDiagnosticsSnapshot
}

/** Every call site guards on the ref being present — this prop is optional
 * everywhere (P71: zero cost for the one remaining caller,
 * `ChainEditorModal`, that does not pass it at all). */
export function patchEditorDiagnostics(
  ref: EditorDiagnosticsRef | null | undefined,
  patch: Partial<EditorDiagnosticsSnapshot>,
): void {
  if (!ref) return
  ref.current = { ...ref.current, ...patch }
}

/** P64/P8 — collapses an arbitrary (possibly URL-bearing) source-load
 * failure message into one of a small fixed set of safe classes for
 * diagnostics. Never returns the input string verbatim. */
export function classifySourceErrorMessage(message: string | null): string | null {
  if (!message) return null
  if (/timed?\s*out/i.test(message)) return 'NETWORK_TIMEOUT'
  if (/media fetch failed:\s*4\d\d/.test(message)) return 'HTTP_4XX'
  if (/media fetch failed:\s*5\d\d/.test(message)) return 'HTTP_5XX'
  if (/media fetch failed/i.test(message)) return 'HTTP_ERROR'
  if (/failed to fetch/i.test(message)) return 'NETWORK_ERROR'
  if (/decode failed/i.test(message)) return 'DECODE_FAILED'
  // VistoeraTASK-00412 — `require2dContext`'s own deliberate, clear
  // message (see `canvasReadinessGuard.ts`) for the "browser's 2D canvas
  // context budget exhausted" class this task root-caused.
  if (/canvas 2d context unavailable/i.test(message)) return 'CANVAS_CONTEXT_UNAVAILABLE'
  return 'OTHER'
}

/** P30-P32 — a lazy-imported chunk that 404s/fails after a redeploy is a
 * DEPLOYMENT/CACHE artifact, not an editor pixel/lifecycle bug. Detected
 * purely from the thrown error's own message — every major bundler
 * (Vite/Rollup, webpack, and Safari's own module-script error text) uses
 * one of these well-known phrasings for exactly this failure, so this is
 * evidence-based classification, never a guess (P1/P58). */
export function isDynamicImportChunkFailure(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error ?? '')
  return (
    /failed to fetch dynamically imported module/i.test(message) ||
    /error loading dynamically imported module/i.test(message) ||
    /loading chunk [\w.-]+ failed/i.test(message) ||
    /importing a module script failed/i.test(message)
  )
}
